/**
 * The saved fields: the register, not the draft.
 *
 * The main product has no equivalent — it draws ONE editable contour in a modal and never a
 * list of them — so this is new code rather than a port.
 *
 * Each field is two paths in two panes: a dark casing underneath and the outline over it.
 * The reason is the same one the lettering has a halo — an unselected outline at #f0e9df /
 * 0.65 opacity is 1.53:1 over sunlit stubble, which is to say it is not there. Only the
 * upper path takes clicks; the casing is deaf so it cannot swallow one.
 *
 * `pmIgnore: true` on both, so Geoman never adopts a saved field: the drawing surface
 * collects the polygons it owns, and a register that quietly became a draft would be saved
 * back as one.
 */

import * as L from "leaflet";

import type { Field } from "../api/types";
import { FIELD_LINE_WIDTH, MAP_LINE_CASING, type MapPalette } from "./basemap";
import { PANES } from "./panes";

export const renderFields = (
  map: L.Map,
  fields: Field[],
  selectedFieldId: number | null,
  palette: MapPalette,
  onSelect: (fieldId: number) => void,
): (() => void) => {
  const layers: L.Layer[] = [];

  for (const field of fields) {
    if (!field.geometry) continue;
    const selected = field.id === selectedFieldId;
    const weight = selected ? FIELD_LINE_WIDTH.selected : FIELD_LINE_WIDTH.plain;

    const casing = L.geoJSON(field.geometry, {
      pane: PANES.fieldsCasing,
      pmIgnore: true,
      interactive: false,
      style: {
        color: MAP_LINE_CASING.color,
        opacity: MAP_LINE_CASING.opacity,
        weight: weight + MAP_LINE_CASING.extraWidth,
        fill: false,
      },
    });

    const outline = L.geoJSON(field.geometry, {
      pane: PANES.fields,
      pmIgnore: true,
      style: {
        className: "k-fieldpath",
        color: selected ? palette.selectedLine : palette.line,
        opacity: selected ? 1 : palette.lineOpacity,
        weight,
        fillColor: selected ? palette.selectedFill : palette.fill,
        fillOpacity: selected ? 0.18 : palette.fillOpacity,
      },
    });
    outline.on("click", () => onSelect(field.id));

    casing.addTo(map);
    outline.addTo(map);
    layers.push(casing, outline);
  }

  return () => {
    for (const layer of layers) map.removeLayer(layer);
  };
};
